import { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  Boxes,
  CheckCircle2,
  CircleDollarSign,
  Compass,
  FileCheck2,
  KeyRound,
  MapPinned,
  Megaphone,
  PackageCheck,
  ShoppingBag,
  Store,
  Users,
} from 'lucide-react';
import { Search } from 'lucide-react';
import {
  getProductHelpArticle,
  getProductHelpArticlesByCategory,
  getProductHelpCategory,
  productHelpArticles,
  productHelpCategories,
  rankProductHelpArticles,
  type ProductHelpArticle,
  type ProductHelpCategoryId,
} from '@workspace/product-help';

import './user-docs.css';
import { ConnectPlaneShell } from './ConnectPlaneShell';
import { developerDocumentationUrl } from './documentation';

type UserDocumentationSiteProps = {
  path?: string;
};

const objects = [
  {
    name: 'Tenant',
    group: 'Context',
    definition: 'The organization you are acting for. Your active tenant scopes the workspaces, records, and actions available to you.',
    boundary: 'Switching tenants changes context; it does not move records between organizations.',
    icon: Users,
  },
  {
    name: 'Catalog',
    group: 'Product identity',
    definition: 'An owner-managed collection of products. A tenant can keep multiple catalogs for different lines of business.',
    boundary: 'A catalog owns product truth, not storefront targeting or geographic availability.',
    icon: BookOpen,
  },
  {
    name: 'Product',
    group: 'Product identity',
    definition: 'The buyer-visible concept, such as Business Internet or a managed router, before a precise configuration is selected.',
    boundary: 'Products carry shared identity and presentation; specific configurations belong to variants.',
    icon: ShoppingBag,
  },
  {
    name: 'Variant',
    group: 'Product identity',
    definition: 'A concrete merchandise or technical configuration of a product, such as a speed tier, size, or hardware model.',
    boundary: 'A variant answers “what is it?” Billing cadence and commitment belong to offers.',
    icon: Boxes,
  },
  {
    name: 'Offer',
    group: 'Commercial terms',
    definition: 'A way to acquire a variant: purchase, rental, installment, or another commercial arrangement.',
    boundary: 'Offers own acquisition and billing terms. They do not redefine the underlying variant.',
    icon: PackageCheck,
  },
  {
    name: 'Pricebook',
    group: 'Commercial terms',
    definition: 'The selling prices and fees applied to offers in a commercial context.',
    boundary: 'A pricebook can change what an offer costs without changing the product, variant, or offer identity.',
    icon: CircleDollarSign,
  },
  {
    name: 'Market',
    group: 'Availability',
    definition: 'A managed service area used to evaluate whether and how a customer location can be served.',
    boundary: 'Markets answer where an offer is available; they do not decide which audience can see a catalog.',
    icon: MapPinned,
  },
  {
    name: 'Publication',
    group: 'Selling experience',
    definition: 'A configured view of a catalog for a selling route, including default and market-specific catalog, pricing, and merchandising choices.',
    boundary: 'A publication exposes product choices to an audience. Eligibility still resolves independently.',
    icon: BookOpen,
  },
  {
    name: 'Storefront',
    group: 'Selling experience',
    definition: 'The operator-facing selling experience that connects a runtime entry point to its active publication and readiness checks.',
    boundary: 'A storefront assembles existing records; catalog editing remains in the Catalogs workspace.',
    icon: Store,
  },
  {
    name: 'Quote',
    group: 'Transaction',
    definition: 'A time-bounded proposal that snapshots customer, eligibility, selected offers, pricing, and approvals.',
    boundary: 'A quote preserves what was proposed even if the source catalog or prices change later.',
    icon: FileCheck2,
  },
  {
    name: 'Order',
    group: 'Transaction',
    definition: 'The accepted commercial commitment used to coordinate fulfillment and preserve what the customer bought.',
    boundary: 'An order retains its own historical snapshots rather than silently following later catalog edits.',
    icon: PackageCheck,
  },
];

function UserDocsLayout({ children }: { children: React.ReactNode }) {
  const footer = (
    <footer className="user-docs-footer">
        <span>ConnectPlane Guide</span>
        <a href="/docs/concepts">Concepts</a>
        <a href="/docs/guides">Guides</a>
        <a href="/docs/changelog">Portal changelog</a>
        <a href={developerDocumentationUrl(window.location.hostname)}>Developer documentation</a>
        <a href="/">ConnectPlane home</a>
    </footer>
  );

  return (
    <ConnectPlaneShell
      surface="user-docs"
      sectionLabel="Guide"
      brandHref="/docs"
      brandLabel="ConnectPlane user guide home"
      activeItemId="user-guides"
      mainContentId="user-docs-main"
      topbarActions={<a className="user-docs-portal" href={portalHref('/dashboard')}>Open ConnectPlane <ArrowRight aria-hidden="true" /></a>}
      footer={footer}
    >
      <main id="user-docs-main" className="user-docs-main">{children}</main>
    </ConnectPlaneShell>
  );
}
const categoryIcons: Record<ProductHelpCategoryId, typeof Compass> = {
  start: Compass,
  'account-access': Users,
  commerce: ShoppingBag,
  maps: MapPinned,
  automation: KeyRound,
  troubleshooting: CheckCircle2,
};

function portalHref(path: string) {
  if (path.startsWith('https://docs.connectplane.com')) return developerDocumentationUrl(window.location.hostname);
  if (path.startsWith('https://')) return path;
  const origins: Record<string, string> = {
    'dev.connectplane.com': 'https://myaccount.dev.connectplane.com',
    'staging.connectplane.com': 'https://myaccount.staging.connectplane.com',
    'connectplane.com': 'https://myaccount.connectplane.com',
    'www.connectplane.com': 'https://myaccount.connectplane.com',
  };
  const origin = origins[window.location.hostname.toLowerCase()] ?? 'http://localhost:5180';
  return `${origin}${path}`;
}

function GuideCard({ article }: { article: ProductHelpArticle }) {
  const Icon = categoryIcons[article.category];
  return (
    <a className="user-guide-card" href={`/docs/guides/${article.id}`}>
      <Icon aria-hidden="true" />
      <span className="user-guide-time">{getProductHelpCategory(article.category).title}</span>
      <h3>{article.title}</h3>
      <p>{article.summary}</p>
      <strong>Read guide <ArrowRight aria-hidden="true" /></strong>
    </a>
  );
}

function ProductHelpSearch({ query, onChange }: { query: string; onChange: (query: string) => void }) {
  return (
    <label className="product-help-search">
      <Search aria-hidden="true" />
      <input
        type="search"
        aria-label="Search Product Help"
        value={query}
        onChange={(event) => onChange(event.target.value)}
        placeholder="What do you want to do?"
      />
    </label>
  );
}

function UserDocsHome() {
  const [query, setQuery] = useState('');
  const results = useMemo(
    () => query.trim() ? rankProductHelpArticles(query) : rankProductHelpArticles('', 4),
    [query],
  );

  return <UserDocsLayout>
    <section className="user-docs-hero product-help-hero">
      <div>
        <p className="eyebrow">ConnectPlane Product Help</p>
        <h1>Find the next useful step.</h1>
        <p>Search by task, browse a product area, or start with a popular guide. Every article focuses on what to do, what success looks like, and how to recover.</p>
        <ProductHelpSearch query={query} onChange={setQuery} />
        <div className="user-docs-actions">
          <a className="button secondary" href="/docs/concepts">Understand ConnectPlane concepts</a>
          <a className="button secondary" href={developerDocumentationUrl(window.location.hostname)}>Developer documentation</a>
        </div>
      </div>
      <div className="user-object-map" aria-label="Popular Product Help tasks">
        {rankProductHelpArticles('', 4).map((article) => (
          <a href={`/docs/guides/${article.id}`} key={article.id}>
            <span>{getProductHelpCategory(article.category).title}</span>
            <strong>{article.title}</strong>
            <ArrowRight aria-hidden="true" />
          </a>
        ))}
      </div>
    </section>

    {query.trim() ? (
      <section className="user-docs-section" aria-labelledby="search-results-heading">
        <div className="user-section-heading">
          <p className="eyebrow">Search results</p>
          <h2 id="search-results-heading">{results.length ? `${results.length} useful ${results.length === 1 ? 'guide' : 'guides'}` : 'No matching guide yet'}</h2>
          <p>{results.length ? 'Results are ranked from the shared Product Help catalog.' : 'Try a shorter task phrase, or browse the categories below.'}</p>
        </div>
        {results.length ? <div className="user-guide-grid">{results.map((article) => <GuideCard article={article} key={article.id} />)}</div> : null}
      </section>
    ) : (
      <section className="user-docs-section" aria-labelledby="start-heading">
        <div className="user-section-heading"><p className="eyebrow">Start with an outcome</p><h2 id="start-heading">Popular guides</h2><p>Short paths for the tasks people need most often.</p></div>
        <div className="user-guide-grid">{results.map((article) => <GuideCard article={article} key={article.id} />)}</div>
      </section>
    )}

    <section className="user-docs-section" aria-labelledby="categories-heading">
      <div className="user-section-heading"><p className="eyebrow">Browse</p><h2 id="categories-heading">Help by product area</h2><p>Choose the area closest to the outcome you are working toward.</p></div>
      <div className="product-help-categories">
        {productHelpCategories.map((category) => {
          const Icon = categoryIcons[category.id];
          const count = getProductHelpArticlesByCategory(category.id).length;
          return <a href={`/docs/categories/${category.id}`} key={category.id}>
            <Icon aria-hidden="true" />
            <span><strong>{category.title}</strong><small>{category.description}</small></span>
            <em>{count} {count === 1 ? 'guide' : 'guides'}</em>
          </a>;
        })}
      </div>
    </section>
  </UserDocsLayout>;
}
function ConceptsPage() {
  const groups = [...new Set(objects.map((object) => object.group))];
  return <UserDocsLayout>
    <section className="user-docs-page-intro">
      <p className="eyebrow">Concepts</p>
      <h1>The ConnectPlane object model</h1>
      <p>A first-class object has its own identity, lifecycle, and responsibility. Understanding those boundaries makes setup faster and prevents one business decision from being encoded in the wrong place.</p>
    </section>

    <section className="relationship-path" aria-labelledby="relationship-heading">
      <div><p className="eyebrow">The short version</p><h2 id="relationship-heading">From product idea to fulfilled order</h2></div>
      <ol>
        <li><span>1</span><strong>Define</strong><p>A tenant owns catalogs. Catalogs contain products, variants, and offers.</p></li>
        <li><span>2</span><strong>Make sellable</strong><p>Pricebooks set commercial values while markets describe availability.</p></li>
        <li><span>3</span><strong>Publish</strong><p>Publications choose the catalog experience; storefronts expose a ready route.</p></li>
        <li><span>4</span><strong>Transact</strong><p>Quotes snapshot the resolved proposal and accepted quotes become orders.</p></li>
      </ol>
    </section>

    <section className="object-reference" aria-label="ConnectPlane first-class objects">
      {groups.map((group) => <div className="object-group" key={group}>
        <h2>{group}</h2>
        <div className="object-grid">
          {objects.filter((object) => object.group === group).map(({ name, definition, boundary, icon: Icon }) => <article className="object-card" id={name.toLowerCase()} key={name}>
            <Icon aria-hidden="true" />
            <h3>{name}</h3>
            <p>{definition}</p>
            <p className="object-boundary"><strong>Keep in mind</strong>{boundary}</p>
          </article>)}
        </div>
      </div>)}
    </section>

    <aside className="user-docs-note"><KeyRound aria-hidden="true" /><div><h2>Your active tenant is always the first boundary.</h2><p>If a page is empty or an action is unavailable, confirm the tenant shown in the portal header and the access granted to your role before changing configuration.</p></div></aside>
  </UserDocsLayout>;
}

function GuidesPage({ categoryId }: { categoryId?: ProductHelpCategoryId }) {
  const articles = categoryId ? getProductHelpArticlesByCategory(categoryId) : productHelpArticles;
  const category = categoryId ? getProductHelpCategory(categoryId) : null;
  return <UserDocsLayout>
    <section className="user-docs-page-intro">
      <p className="eyebrow">{category ? 'Product Help category' : 'Product Help guides'}</p>
      <h1>{category?.title ?? 'Follow the work, step by step.'}</h1>
      <p>{category?.description ?? 'Each guide gives you a clear goal, concise steps, an expected result, and recovery guidance.'}</p>
      <div className="user-docs-actions"><a className="button secondary" href="/docs">Search Product Help</a></div>
    </section>
    <section className="user-guide-grid user-guide-index" aria-label="ConnectPlane Product Help guides">
      {articles.map((article) => <GuideCard article={article} key={article.id} />)}
    </section>
  </UserDocsLayout>;
}

const portalChanges = [
  {
    date: '2026-10-22',
    displayDate: 'October 22, 2026',
    title: 'More reliable sign-in and API access',
    summary: 'Portal sessions now recover more cleanly when authentication needs to be renewed.',
    changes: [
      'Prevented the API Access workspace from repeatedly sending users through reauthentication.',
      'Kept the active portal destination intact while the sign-in session is renewed.',
    ],
  },
  {
    date: '2026-10-21',
    displayDate: 'October 21, 2026',
    title: 'Email-first sign-in and tenant selection',
    summary: 'Signing in now begins with your email address and guides you into the organization you intend to manage.',
    changes: [
      'Added email-first account discovery before authentication.',
      'Added an explicit tenant choice when an account belongs to more than one organization.',
      'Preserved the requested destination through sign-in and tenant selection.',
    ],
  },
  {
    date: '2026-10-20',
    displayDate: 'October 20, 2026',
    title: 'A clearer quote workspace',
    summary: 'Quote work now shows progress, products, revisions, and approval state in one more coherent workflow.',
    changes: [
      'Added a quote workflow dashboard with current status and next actions.',
      'Redesigned product selection and kept product option types intact.',
      'Exposed quote revisions and added the approval signing workflow.',
    ],
  },
  {
    date: '2026-10-20',
    displayDate: 'October 20, 2026',
    title: 'Slack notification channels',
    summary: 'Administrators can configure Slack webhooks as notification destinations in the portal.',
    changes: [
      'Added Slack as a notification channel type.',
      'Kept routing and delivery activity visible alongside existing notification configuration.',
    ],
  },
  {
    date: '2026-10-15',
    displayDate: 'October 15, 2026',
    title: 'Sales map build visibility',
    summary: 'Map operators can follow long-running sales map work and review fiber-distance results more confidently.',
    changes: [
      'Added progress reporting while a sales map is rebuilt.',
      'Added fiber-distance map generation from location and routing data.',
      'Improved large source-list handling in the map setup workflow.',
    ],
  },
];

function PortalChangelog() {
  return <UserDocsLayout>
    <section className="user-docs-page-intro portal-changelog-intro">
      <p className="eyebrow">App portal dashboard</p>
      <h1>Portal changelog</h1>
      <p>User-visible dashboard features, workflow improvements, and fixes appear here. Integration and compatibility changes are tracked separately in the <a href="/docs/api/changelog">external API changelog</a>.</p>
    </section>
    <section className="portal-changelog" aria-label="Portal updates">
      {portalChanges.map((entry) => <article className="portal-change-entry" key={`${entry.date}-${entry.title}`}>
        <div className="portal-change-date"><Megaphone aria-hidden="true" /><time dateTime={entry.date}>{entry.displayDate}</time></div>
        <div><h2>{entry.title}</h2><p>{entry.summary}</p><ul>{entry.changes.map((change) => <li key={change}>{change}</li>)}</ul></div>
      </article>)}
    </section>
  </UserDocsLayout>;
}

function ProductHelpArticlePage({ articleId }: { articleId: string }) {
  const article = getProductHelpArticle(articleId);

  if (!article) {
    return <UserDocsLayout>
      <section className="user-docs-page-intro">
        <p className="eyebrow">Product Help</p>
        <h1>We couldn’t find that guide.</h1>
        <p>The link may be old or incomplete. Browse the current guides or search by the task you want to complete.</p>
        <div className="user-docs-actions"><a className="button primary" href="/docs">Search Product Help</a><a className="button secondary" href="/docs/guides">Browse all guides</a></div>
      </section>
    </UserDocsLayout>;
  }

  const related = article.relatedArticleIds
    .map((id) => getProductHelpArticle(id))
    .filter((candidate): candidate is ProductHelpArticle => Boolean(candidate));

  return <UserDocsLayout>
    <article className="user-guide-article product-help-article">
      <header>
        <p className="eyebrow">{getProductHelpCategory(article.category).title}</p>
        <h1>{article.title}</h1>
        <p>{article.goal}</p>
      </header>

      {article.sections.map((section) => <section className="guide-steps" key={section.id}>
        <h2>{section.title}</h2>
        {section.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
        {section.steps ? <ol>{section.steps.map((step, index) => <li key={step.title}><span>{index + 1}</span><div><h3>{step.title}</h3><p>{step.body}</p></div></li>)}</ol> : null}
      </section>)}

      <section className="guide-outcome"><CheckCircle2 aria-hidden="true" /><div><p className="eyebrow">Expected result</p><h2>{article.expectedResult}</h2></div></section>

      <section className="product-help-troubleshooting">
        <p className="eyebrow">Troubleshooting</p>
        <h2>If things don’t look right</h2>
        <div>{article.troubleshooting.map((item) => <article key={item.title}><h3>{item.title}</h3><p>{item.body}</p></article>)}</div>
      </section>

      <section className="product-help-actions">
        <div><p className="eyebrow">Continue in ConnectPlane</p><h2>Put the guide into action</h2></div>
        <div>{article.actions.map((action) => <a className="button primary" href={portalHref(action.href)} key={action.label}>{action.label}<ArrowRight aria-hidden="true" /></a>)}</div>
      </section>

      <section className="product-help-related">
        <p className="eyebrow">Related guides</p>
        <div>{related.map((item) => <GuideCard article={item} key={item.id} />)}</div>
      </section>

      <footer className="product-help-review">Reviewed {article.lastReviewed} · Owner: {article.owner} · Review due {article.reviewAfter}</footer>
    </article>
  </UserDocsLayout>;
}

export function UserDocumentationSite({ path = window.location.pathname }: UserDocumentationSiteProps) {
  const [pathname, search = ''] = path.split('?');
  const params = new URLSearchParams(search);
  const initialSearch = params.get('search') ?? '';

  useEffect(() => {
    const articleId = pathname?.startsWith('/docs/guides/') ? pathname.slice('/docs/guides/'.length) : null;
    const article = articleId ? getProductHelpArticle(articleId) : null;
    const isChangelog = pathname === '/docs/changelog';
    document.title = article ? `${article.title} · ConnectPlane Product Help` : isChangelog ? 'ConnectPlane Portal Changelog' : 'ConnectPlane Product Help';
    document.querySelector('meta[name="description"]')?.setAttribute('content', article?.summary ?? (isChangelog
      ? 'User-visible ConnectPlane portal dashboard features, workflow improvements, and fixes.'
      : 'Task-focused ConnectPlane Product Help for account, commerce, maps, API access, and troubleshooting.'));
  }, [pathname]);

  if (pathname === '/docs/concepts') return <ConceptsPage />;
  if (pathname === '/docs/guides') return <GuidesPage />;
  if (pathname === '/docs/changelog') return <PortalChangelog />;
  if (pathname?.startsWith('/docs/categories/')) {
    const categoryId = pathname.slice('/docs/categories/'.length) as ProductHelpCategoryId;
    return productHelpCategories.some((category) => category.id === categoryId) ? <GuidesPage categoryId={categoryId} /> : <UserDocsHome />;
  }
  if (pathname?.startsWith('/docs/guides/')) {
    const requestedId = pathname.slice('/docs/guides/'.length);
    const legacyPublicIds: Record<string, string> = {
      'publish-a-storefront': 'publish-offers',
      'quote-to-order': 'review-orders',
    };
    return <ProductHelpArticlePage articleId={legacyPublicIds[requestedId] ?? requestedId} />;
  }

  return initialSearch ? <SearchResultsHome initialSearch={initialSearch} /> : <UserDocsHome />;
}

function SearchResultsHome({ initialSearch }: { initialSearch: string }) {
  const [query, setQuery] = useState(initialSearch);
  const results = useMemo(() => rankProductHelpArticles(query), [query]);
  return <UserDocsLayout>
    <section className="user-docs-page-intro">
      <p className="eyebrow">ConnectPlane Product Help</p>
      <h1>Search by task.</h1>
      <p>Describe what you want to do in plain language.</p>
      <ProductHelpSearch query={query} onChange={setQuery} />
    </section>
    <section className="user-docs-section">
      <div className="user-section-heading"><h2>{results.length ? 'Relevant guides' : 'No matching guide yet'}</h2><p>{results.length ? 'The closest task-focused guidance appears first.' : 'Try fewer words or browse all Product Help guides.'}</p></div>
      {results.length ? <div className="user-guide-grid">{results.map((article) => <GuideCard article={article} key={article.id} />)}</div> : <a className="button secondary" href="/docs/guides">Browse all guides</a>}
    </section>
  </UserDocsLayout>;
}
